import React, { useEffect, useState } from 'react';
import { FaFileAlt, FaFilePdf, FaFileWord, FaFileExcel, FaFilePowerpoint, FaDownload, FaCalendarAlt, FaFolder } from 'react-icons/fa';
import axiosPost from '../../helper/axios-helper';
import '../../styles/AgmDocuments.css';

function AgmDocuments() {
  const [documents, setDocuments] = useState([]);
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [groupedDocuments, setGroupedDocuments] = useState({});

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = () => {
    setLoading(true);
    axiosPost('agm_documents/latest', {}, {}, (data) => {
      const { success, response } = data;
      if (success) {
        setDocuments(data.data || []);
        setEvent(data.event || null);
        
        // Group documents by category
        const grouped = (data.data || []).reduce((acc, doc) => {
          const category = doc.category || 'general';
          if (!acc[category]) {
            acc[category] = [];
          }
          acc[category].push(doc);
          return acc;
        }, {});
        setGroupedDocuments(grouped);
      } else {
        console.log("Error fetching AGM documents:", response);
      }
      setLoading(false);
    });
  };

  const getFileIcon = (fileType) => {
    if (!fileType) return <FaFileAlt />;
    
    if (fileType.includes('pdf')) {
      return <FaFilePdf className="file-icon pdf" />;
    } else if (fileType.includes('word') || fileType.includes('doc')) {
      return <FaFileWord className="file-icon word" />;
    } else if (fileType.includes('excel') || fileType.includes('sheet') || fileType.includes('xls')) {
      return <FaFileExcel className="file-icon excel" />;
    } else if (fileType.includes('powerpoint') || fileType.includes('presentation') || fileType.includes('ppt')) {
      return <FaFilePowerpoint className="file-icon powerpoint" />;
    }
    return <FaFileAlt className="file-icon" />;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatCategoryName = (category) => {
    return category
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="agm-documents-container">
      <div className="agm-documents-header">
        <div className="agm-documents-header-content">
          <h1>AGM Documents</h1>
          <div className="agm-documents-divider"></div>
          {event && (
            <div className="agm-documents-event-info">
              <FaCalendarAlt className="event-icon" />
              <span>{event.name || event.title}</span>
            </div>
          )}
          <p className="agm-documents-description">
            Access official documents from the CANTO Annual General Meeting including minutes, reports, resolutions, and other important materials.
          </p>
        </div>
      </div>

      <div className="agm-documents-content">
        {loading ? (
          <div className="agm-documents-loading">
            <div className="loading-spinner"></div>
            <p>Loading documents...</p>
          </div>
        ) : documents.length === 0 ? (
          <div className="agm-documents-empty">
            <FaFolder className="empty-icon" />
            <h3>No Documents Available</h3>
            <p>AGM documents will be available here once they are published.</p>
          </div>
        ) : (
          <div className="agm-documents-grid">
            {Object.keys(groupedDocuments).map((category) => (
              <div key={category} className="agm-documents-category">
                <h2 className="category-title">
                  <FaFolder className="category-icon" />
                  {formatCategoryName(category)}
                </h2>
                <div className="documents-list">
                  {groupedDocuments[category].map((doc) => (
                    <a 
                      key={doc.id} 
                      href={doc.file_path} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="document-card"
                    >
                      <div className="document-icon">
                        {getFileIcon(doc.file_type)}
                      </div>
                      <div className="document-info">
                        <h3 className="document-title">{doc.title}</h3>
                        {doc.description && (
                          <p className="document-description">{doc.description}</p>
                        )}
                        <div className="document-meta">
                          <span className="document-filename">{doc.file_name}</span>
                          {doc.file_size && (
                            <span className="document-size">{formatFileSize(doc.file_size)}</span>
                          )}
                        </div>
                      </div>
                      <div className="document-download">
                        <FaDownload />
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default AgmDocuments;
